import { type IUser } from '@/types/IUser';
import { type ResponseCheckUser } from '@/types/ICheckUser';
import { api } from '../utils/api';
import axios, { isAxiosError } from 'axios';
import { store } from '@/store/store';
import { setUserChecked } from '@/store/auth';
import { clearAccountData } from '@/store/account';
import { refreshTokenManager } from './refreshTokenService';
import { clearLastEventId } from './mercure/lastEventIdStorage';
import { clearUnviewed } from '@/store/unviewedOrders';
import { markAllReadOnLogout } from '@/store/notifications';

export const AUTH_TOKEN = 'authToken';
export const REFRESH_TOKEN = 'refreshToken';
export const REFRESH_KEY = 'refreshKey';
export const REMEMBER_ME = 'rememberMe';
export const USER = 'user';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AuthService {
  private static isLoggingOut = false;

  static logout(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this.authToken = null;
    this.refreshKey = null;
    this.refreshToken = null;
    this.rememberMe = null;
    this.user = null;

    store.dispatch(setUserChecked(false));
    store.dispatch(clearAccountData());

    clearLastEventId();
    // Непросмотренные (favicon/счётчик/подсветка) обнуляем — после перелогина
    // эти данные неактуальны. Историю-ленту НЕ удаляем, но помечаем прочитанной.
    store.dispatch(clearUnviewed());
    store.dispatch(markAllReadOnLogout());

    void refreshTokenManager.reset();

    window.location.href = '/login';
  }

  public static get authToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN);
  }

  public static set authToken(value: string | null) {
    this.setLocalStorageItem(AUTH_TOKEN, value);
  }

  public static get refreshKey(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  }

  public static set refreshKey(value: string | null) {
    this.setLocalStorageItem(REFRESH_KEY, value);
  }

  public static get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN);
  }

  public static set refreshToken(value: string | null) {
    this.setLocalStorageItem(REFRESH_TOKEN, value);
  }

  public static get rememberMe(): boolean | null {
    const rememberMe = localStorage.getItem(REMEMBER_ME);

    return rememberMe != null && JSON.parse(rememberMe);
  }

  public static set rememberMe(value: boolean | null) {
    this.setLocalStorageItem(REMEMBER_ME, value);
  }

  public static get isAuthorized(): boolean {
    return !!(
      this.authToken != null &&
      this.refreshKey != null &&
      this.refreshToken != null
    );
  }

  public static get user(): IUser | null {
    const userJson = localStorage.getItem(USER);

    return userJson != null && JSON.parse(userJson);
  }

  public static set user(value: IUser | null) {
    this.setLocalStorageItem(USER, value);
  }

  private static setLocalStorageItem(
    key: string,
    value: IUser | string | boolean | null
  ): void {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(
        key,
        typeof value === 'string' ? value : JSON.stringify(value)
      );
    }
  }

  static async refreshAuthToken(): Promise<
    | undefined
    | {
        refreshToken: string;
        refreshKey: string;
        authToken: string;
      }
  > {
    const initialRefreshToken = this.refreshToken;

    return await refreshTokenManager.executeRefresh(async () => {
      try {
        // eslint-disable-next-line no-console
        console.log('[AuthService] Выполнение refresh запроса');

        if (
          this.rememberMe === undefined ||
          this.rememberMe === null ||
          !this.rememberMe
        ) {
          // eslint-disable-next-line no-console
          console.log(
            '[AuthService] Refresh отклонен: rememberMe не установлен'
          );
          throw new Error('Вы не авторизованы');
        }

        const refreshUrl =
          import.meta.env.VITE_API_URL + '/api/public/login/refresh/v2';

        const refresh = await axios.post(
          refreshUrl,
          {
            access_token: this.authToken,
            refresh_token: this.refreshToken,
            refresh_key: this.refreshKey
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (refresh.data === null || refresh.data === undefined) {
          throw new Error('При попытке обновить токен произошла ошибка');
        }

        if (
          refresh.data.response === undefined ||
          refresh.data.response === null
        ) {
          throw new Error('При попытке обновить токен произошла ошибка');
        }

        if (
          refresh.data.meta.error !== undefined &&
          refresh.data.meta.error !== null
        ) {
          if (typeof refresh.data.meta.error === 'object') {
            throw new Error(refresh.data.meta.error.message);
          }
          if (typeof refresh.data.meta.error === 'string') {
            throw new Error(refresh.data.meta.error);
          }
        }

        if (this.refreshToken !== initialRefreshToken) {
          // eslint-disable-next-line no-console
          console.warn(
            '[AuthService] Токены изменились во время refresh, игнорируем результат'
          );
          throw new Error('Сессия изменилась во время обновления токена');
        }

        this.refreshToken = refresh.data.response.refresh_token.token;
        this.refreshKey = refresh.data.response.refresh_token.key;
        this.authToken = refresh.data.response.access_token;

        // eslint-disable-next-line no-console
        console.log('[AuthService] Токены успешно обновлены');

        return {
          refreshToken: refresh.data.response.refresh_token.token,
          refreshKey: refresh.data.response.refresh_token.key,
          authToken: refresh.data.response.access_token
        };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AuthService] Ошибка refresh:', err);

        const isFatal = this.isFatalRefreshError(err);

        if (isFatal) {
          this.logout();
        }

        throw err;
      }
    });
  }

  /**
   * Определяет, является ли ошибка refresh фатальной (требует разлогина).
   *
   * Принцип: разлогиниваем пользователя ТОЛЬКО когда сервер явно сообщил,
   * что токены невалидны (HTTP 401/403 на самом refresh-запросе). Любая
   * другая ошибка — временная серверная (5xx), сетевая, либо исключение в
   * нашем собственном коде (например, ошибка разбора ответа) — НЕ фатальна:
   * сессия при этом валидна, пользователя выкидывать нельзя.
   *
   * Так баг во фронте (неверный парсинг, неожиданный формат ответа) приводит
   * к проваленному refresh, но не к принудительному logout живой сессии.
   *
   * Чтобы понять, почему пользователя разлогинило, ищите здесь: фатальным
   * может стать ТОЛЬКО ветка `isAxiosError(error)` со статусом 401/403.
   */
  private static isFatalRefreshError(error: unknown): boolean {
    // (б) Не-axios ошибка = исключение в нашем коде (парсинг ответа, баг и т.п.).
    // Это не признак невалидной сессии — не фатально.
    if (!isAxiosError(error)) {
      return false;
    }

    const status: number | undefined = error.response?.status;

    // (а) Фатально только при явном отказе сервера по токенам.
    // Всё остальное (5xx, таймауты, сеть, неожиданные 4xx) — не фатально.
    return status === 401 || status === 403;
  }

  static async checkUser(): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log('[AuthService] Проверка пользователя');

      const { data } = await api.get<ResponseCheckUser>('/private/account', {
        headers: {
          Authorization: `Bearer ${this.authToken}`
        }
      });

      if (data.meta.error !== undefined && data.meta.error !== null) {
        throw new Error(data.meta.error);
      }

      if (data !== undefined && data.meta.error !== undefined) {
        this.user = data.response;
        // eslint-disable-next-line no-console
        console.log('[AuthService] Пользователь успешно проверен');
      }
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        const status = err.response?.status;

        if (status === 401) {
          // eslint-disable-next-line no-console
          console.log(
            '[AuthService] 401 при checkUser, обработка через interceptor'
          );
        } else if (status === 403) {
          // eslint-disable-next-line no-console
          console.log('[AuthService] 403 Forbidden, выполнение logout');
          this.logout();
        } else if (status !== undefined && status >= 500) {
          // eslint-disable-next-line no-console
          console.error('[AuthService] Ошибка сервера при checkUser:', status);
        } else if (
          err.code === 'ERR_NETWORK' ||
          err.message.includes('Network Error')
        ) {
          // eslint-disable-next-line no-console
          console.error('[AuthService] Сетевая ошибка при checkUser');
        } else if (status !== undefined && status >= 400) {
          // eslint-disable-next-line no-console
          console.error(
            '[AuthService] Клиентская ошибка при checkUser:',
            status
          );
          this.logout();
        }
      } else {
        // eslint-disable-next-line no-console
        console.error(
          '[AuthService] Неизвестная ошибка при проверке пользователя:',
          err
        );
        this.logout();
      }
    }
  }

  static get accountData(): IUser | null {
    return this.user;
  }
}
